import { ErrorNotification, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, Form, Modal, Popconfirm, Row, Space } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { DriverContact } from './Contact'
import { CreateHeader } from './CreateHeader'
import { Depot } from './Depot'
import { DriverSchedule } from './DriverSchedule'
import { DriverHeader } from './Header'
import { PWModalContent } from './PWModalContent'
import { SourceInfo } from './Source'
import { Vehicle } from './Vehicle'

import { ExclamationCircleFilled } from '@ant-design/icons'
import { useDriverManagement } from '@api/useDriverManagement'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { dateFormat } from '@constants/dateFormat'
import { roundToNearestHalf } from '@utils/general'
import moment from 'moment'
import {
  addCreateSaveValues,
  addUpdateSaveValues,
  checkIfNeedsScheduleGeneration,
  getTripModeText,
  saveDriver,
} from './util'

export const DriverForm = ({
  selectedDriver,
  driver,
  setDriver,
  setLoading,
  payConfigNames,
  metadata,
  creatingDriver,
  setSelectedDriver,
  setCreatingDriver,
  reloadData,
  editing,
  setEditing,
  viewingPWModal,
  setViewingPWModal,
}) => {
  const getTemplate = useCallback(
    (templateName) => metadata?.templates?.find((t) => t.name === templateName),
    [metadata.templates]
  )

  const { scopes } = useSecurityContext()
  const [showWarning, setShowWarning] = useState(false)
  const [startTimeOverridden, setStartTimeOverridden] = useState(false)
  const [needsScheduleGenerated, setNeedsScheduleGenerated] = useState(false)
  const [warningConfirmed, setWarningConfirmed] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isPatternStartRequired, setPatternStartRequired] = useState(
    getTemplate(driver?.preferred_template)?.is_pattern
  )
  const { useDriverUpdate } = useDriverManagement()
  const useUpsertDriverMutation = useDriverUpdate()

  useEffect(() => {
    setPatternStartRequired(getTemplate(driver?.preferred_template)?.is_pattern)
  }, [selectedDriver])

  const [form] = Form.useForm()

  const canEditDriverInfo = scopes.driver?.info.edit
  const canOnlyChangePassword = !canEditDriverInfo && scopes.scopes.includes('di.p')

  useEffect(() => {
    form.setFieldsValue({
      ...driver,
      hire_date: driver?.hire_date ? moment(driver.hire_date) : moment(),
      termination_date: driver?.termination_date ? moment(driver.termination_date) : undefined,
      am_shift_start_time: driver?.am_shift_start_time ? moment(driver.am_shift_start_time) : undefined,
      pm_shift_start_time: driver?.pm_shift_start_time ? moment(driver.pm_shift_start_time) : undefined,
      shift_start_time:
        driver?.shift_preference === 'am'
          ? driver?.am_shift_start_time
            ? moment(driver.am_shift_start_time)
            : undefined
          : driver?.shift_preference === 'pm' && driver?.pm_shift_start_time
            ? moment(driver.pm_shift_start_time)
            : undefined,
      shift_length_hours: driver?.shift_length?.shift_length_hours,
      pattern_schedule_start_date: driver?.pattern_schedule_start_date
        ? moment(driver.pattern_schedule_start_date)
        : undefined,
      in_cab_trip_mode: getTripModeText(driver?.in_cab_trip_mode),
    })
  }, [driver, editing])

  const confirmWarning = () => {
    setWarningConfirmed(true)
    form.submit()
  }
  const resetFormState = (isCatch) => {
    setCreatingDriver(false)
    setShowWarning(false)
    setWarningConfirmed(false)
    setIsSaving(false)
    if (!isCatch) {
      setEditing(false)
      setStartTimeOverridden(false)
      setNeedsScheduleGenerated(false)
    }
  }

  const upsertUpdatedDriver = (resp) => {
    reloadData().then((driverList) => {
      setLoading(false)
      if (!resp?.data?.id) return
      setSelectedDriver(driverList.find((d) => d.id === resp.data.id))
    })
  }
  const completeSave = (formData) => {
    if (!warningConfirmed) {
      if (checkIfNeedsScheduleGeneration(formData, driver, creatingDriver)) {
        setNeedsScheduleGenerated(true)
        return
      }
      if (!formData.active && driver?.active) {
        setShowWarning(true)
        return
      }
    }
    if (!driver) {
      setLoading(true)
    }

    if (formData.pattern_schedule_start_date) {
      formData.pattern_schedule_start_date = moment(formData.pattern_schedule_start_date).format(
        dateFormat.DATE_ZERO_TIME
      )
    }
    if (formData.shift_preference) {
      formData.shift_preference = formData.shift_preference.toLowerCase()
    } else {
      formData.shift_preference = null
    }
    if (startTimeOverridden) {
      formData.am_shift_start_time = formData.shift_preference === 'am' ? formData.shift_start_time : undefined
      formData.pm_shift_start_time = formData.shift_preference === 'pm' ? formData.shift_start_time : undefined
    }
    if (formData.shift_length_hours && driver === undefined) {
      formData.shift_length_hours = roundToNearestHalf(formData.shift_length_hours)
    }
    if (
      driver &&
      formData.shift_length_hours &&
      formData.shift_length_hours !== driver.shift_length.shift_length_hours
    ) {
      formData.shift_length_hours = roundToNearestHalf(formData.shift_length_hours)
    }
    if (formData.in_cab_trip_mode) {
      switch (formData.in_cab_trip_mode) {
        case 'View All':
          formData.in_cab_trip_mode = 'all'
          break
        case 'View All (Restricted)':
          formData.in_cab_trip_mode = 'restricted'
          break
        case 'View All + Next Shift':
          formData.in_cab_trip_mode = 'all and next'
          break
        default:
          formData.in_cab_trip_mode = 'current'
          break
      }
    }
    const updatedDriver = driver
      ? addUpdateSaveValues(formData, driver, startTimeOverridden)
      : addCreateSaveValues(formData, startTimeOverridden)
    setIsSaving(true)
    saveDriver(
      driver,
      updatedDriver,
      setDriver,
      needsScheduleGenerated,
      resetFormState,
      upsertUpdatedDriver,
      useUpsertDriverMutation
    )
  }

  return (
    <>
      <Card
        className='px-4'
        extra={
          (creatingDriver || canEditDriverInfo || scopes.scopes.includes('di.p')) && (
            <ActionDriverInfoButtons
              editing={editing}
              setEditing={setEditing}
              showWarning={showWarning}
              driver={driver}
              setViewingPWModal={setViewingPWModal}
              needsScheduleGenerated={needsScheduleGenerated}
              setNeedsScheduleGenerated={setNeedsScheduleGenerated}
              setShowWarning={setShowWarning}
              confirmWarning={confirmWarning}
              form={form}
              isSaving={isSaving}
              canOnlyChangePassword={canOnlyChangePassword}
            />
          )
        }
      >
        <Form
          form={form}
          id='driver-info-form'
          className='driver-info-form'
          onFinish={(values) => completeSave(values)}
          onFinishFailed={(msg) => ErrorNotification(msg)}
          onValuesChange={(changedValues) => {
            if (changedValues.preferred_template) {
              setPatternStartRequired(
                metadata.templates.find((option) => option.name === changedValues.preferred_template)?.is_pattern
              )
            }
          }}
        >
          <div className='driver-display pr-4 pl-1 pb-5'>
            {creatingDriver && <CreateHeader setCreatingDriver={setCreatingDriver} />}

            <DriverHeader
              driver={driver}
              payConfigNames={creatingDriver || payConfigNames}
              editing={editing}
              setEditing={setEditing}
              creatingDriver={creatingDriver}
            />
            <Horizontal className='pt-2' justifyContent={'center'}>
              <Vertical flex={1.5}>
                <Horizontal className='pt-3'>
                  <DriverSchedule
                    form={form}
                    driver={driver}
                    editing={editing}
                    metadata={metadata.templates}
                    isPatternStartRequired={isPatternStartRequired}
                    setStartTimeOverridden={setStartTimeOverridden}
                  />
                </Horizontal>
                <Row className='pt-4'>
                  <Vehicle
                    driver={driver}
                    metadata={metadata}
                    selectedDriver={selectedDriver}
                    setSelectedDriver={setSelectedDriver}
                    editing={editing}
                  />
                </Row>
                <Row className='pt-4'>
                  <SourceInfo driver={driver} editing={editing} />
                </Row>
                <Row className='pt-4'>
                  <DriverContact driver={driver} editing={editing} />
                </Row>
              </Vertical>
              <Vertical flex={1.2} className=''>
                <Depot
                  driver={driver}
                  locations={metadata.locations.filter((l) => l.type === 'Depot')}
                  editing={editing}
                  setSelectedDriver={setSelectedDriver}
                />
              </Vertical>
            </Horizontal>
          </div>
        </Form>
        <Modal
          onCancel={() => setViewingPWModal(false)}
          destroyOnClose
          footer={null}
          title={<Texto>Change Driver Password</Texto>}
          visible={viewingPWModal}
        >
          <PWModalContent setViewingPWModal={setViewingPWModal} driver={driver} />
        </Modal>
      </Card>
    </>
  )
}

const ActionDriverInfoButtons = ({
  editing,
  setEditing,
  showWarning,
  driver,
  setViewingPWModal,
  needsScheduleGenerated,
  setNeedsScheduleGenerated,
  setShowWarning,
  confirmWarning,
  form,
  isSaving,
  canOnlyChangePassword,
}) => {
  return (
    <Space>
      {driver && !editing && (
        <GraviButton
          buttonText='Change Password'
          theme2
          appearance='outline'
          onClick={() => setViewingPWModal(true)}
          data-cy='change-driver-pw-btn'
        />
      )}
      {driver && !canOnlyChangePassword && (
        <GraviButton
          buttonText={editing ? 'CANCEL' : 'Edit Driver Info'}
          theme2
          onClick={() => setEditing(!editing)}
          data-cy='edit-driver-info-btn'
        />
      )}

      {editing && driver && (
        <SaveButton
          isSaving={isSaving}
          form={form}
          driver={driver}
          showWarning={showWarning}
          needsScheduleGenerated={needsScheduleGenerated}
          setShowWarning={setShowWarning}
          setNeedsScheduleGenerated={setNeedsScheduleGenerated}
          confirmWarning={confirmWarning}
        />
      )}
    </Space>
  )
}
const SaveButton = ({
  driver,
  showWarning,
  confirmWarning,
  needsScheduleGenerated,
  setShowWarning,
  setNeedsScheduleGenerated,
  form,
  isSaving,
}) => {
  return (
    <Popconfirm
      placement='bottomRight'
      title={
        needsScheduleGenerated
          ? `Editing schedule fields will wipe auto generated shifts for ${driver.name}'s next schedule periods. 
          Are you sure you want to continue?`
          : `Inactivating will wipe future scheduled shifts and deactivate In-Cab login. 
          Are you sure you want to inactivate ${driver.name}?`
      }
      visible={needsScheduleGenerated || showWarning}
      icon={<ExclamationCircleFilled style={{ color: 'var(--theme-error)' }} />}
      okText='YES'
      cancelText='NO'
      onConfirm={confirmWarning}
      onCancel={() => {
        setNeedsScheduleGenerated(false)
        setShowWarning(false)
      }}
    >
      <GraviButton
        loading={isSaving}
        theme2
        buttonText='SAVE'
        onClick={() => form.submit()}
        htmlType='submit'
        data-cy='save-driver-btn'
      />
    </Popconfirm>
  )
}
